'use client';
import * as React from 'react';
import { Transition } from '@headlessui/react';
import {
  cn,
  SEVERITY_CONFIG,
  severityStyle,
  type Severity,
  type SeverityOrNull
} from '../../utils';
import type { TooltipPosition } from '../../types/TooltipPosition';
import { useTranslation } from '../../providers/UIProvider';

export interface ValidationTooltipIssue {
  /** Accepts any string for compatibility with external sources (e.g. GraphQL enums). */
  severity: string;
  message: string;
}

export interface ValidationTooltipProps {
  /** Primary severity — drives the tooltip's overall color treatment. */
  severity: Severity;
  /** Single pre-formatted summary line. Mutually exclusive with `issues`. */
  label?: string;
  /** List of issues — one row per issue, with each issue's own severity icon. */
  issues?: ValidationTooltipIssue[];
  /** Arrow direction — defaults to `top`. */
  position?: TooltipPosition;
  /** Hover trigger element (the icon or field shown to the user). */
  children: React.ReactNode;
  className?: string;
  testId?: string;
}

const MAX_VISIBLE_ISSUES = 6;

function getPositionClasses(pos: TooltipPosition): string {
  switch (pos) {
    case 'top':
    case 'top center':
      return 'bottom-full left-1/2 -translate-x-1/2 mb-sm';
    case 'top left':
      return 'bottom-full left-0 mb-sm';
    case 'top right':
      return 'bottom-full right-0 mb-sm';
    case 'bottom':
      return 'top-full left-1/2 -translate-x-1/2 mt-sm';
    case 'left':
      return 'right-full top-1/2 -translate-y-1/2 mr-sm';
    case 'right':
      return 'left-full top-1/2 -translate-y-1/2 ml-sm';
    default:
      return 'bottom-full left-1/2 -translate-x-1/2 mb-sm';
  }
}

function getArrowPositionClasses(pos: TooltipPosition): string {
  switch (pos) {
    case 'top':
    case 'top center':
      return 'top-full left-1/2 -translate-x-1/2';
    case 'top left':
      return 'top-full left-4';
    case 'top right':
      return 'top-full right-4';
    case 'bottom':
      return 'bottom-full left-1/2 -translate-x-1/2';
    case 'left':
      return 'left-full top-1/2 -translate-y-1/2';
    case 'right':
      return 'right-full top-1/2 -translate-y-1/2';
    default:
      return 'top-full left-1/2 -translate-x-1/2';
  }
}

/**
 * Severity-themed hover tooltip used for validation messages. Background,
 * border and text inherit from `SEVERITY_CONFIG`, so error/warning surfaces
 * stay visually distinct from the generic dark `Tooltip`.
 *
 * Use `label` for a single pre-formatted summary or `issues` to render one
 * row per issue (each with its own severity icon). When both are provided,
 * `label` wins.
 */
export function ValidationTooltip({
  severity,
  label,
  issues,
  position = 'top',
  children,
  className,
  testId
}: ValidationTooltipProps) {
  const [show, setShow] = React.useState(false);
  const tooltipId = React.useId();
  const t = useTranslation('components.validationTooltip');

  const style = SEVERITY_CONFIG[severity];
  const positionClasses = getPositionClasses(position);
  const arrowClasses = getArrowPositionClasses(position);

  const visibleIssues = issues?.slice(0, MAX_VISIBLE_ISSUES) ?? [];
  const overflowCount = (issues?.length ?? 0) - visibleIssues.length;

  return (
    <div
      className={cn('relative inline-block', className)}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
      aria-describedby={show ? tooltipId : undefined}
      data-testid={testId}
      tabIndex={0}
    >
      {children}

      <Transition
        show={show}
        enter='transition-opacity duration-200'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='transition-opacity duration-150'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'
      >
        <div
          id={tooltipId}
          role='tooltip'
          style={{ minWidth: '360px', maxWidth: '480px' }}
          className={cn(
            'absolute !z-[1111] whitespace-normal rounded-md border shadow-lg',
            'px-md py-sm text-left',
            style.bgColor,
            style.borderColor,
            positionClasses
          )}
        >
          {label ? (
            <p className={cn('text-xs font-medium leading-snug', style.iconColor)}>{label}</p>
          ) : (
            <ul className='flex flex-col gap-sm'>
              {visibleIssues.map((issue, idx) => {
                const issueStyle = severityStyle(issue.severity as SeverityOrNull) ?? style;
                return (
                  <li key={idx} className={cn('text-xs leading-snug', issueStyle.iconColor)}>
                    {issue.message}
                  </li>
                );
              })}
              {overflowCount > 0 && (
                <li className={cn('text-xs italic', style.bodyColor)}>
                  {t('overflowMore', { count: overflowCount })}
                </li>
              )}
            </ul>
          )}
          <div className={cn('absolute w-2 h-2 rotate-45', style.bgColor, arrowClasses)} />
        </div>
      </Transition>
    </div>
  );
}

ValidationTooltip.displayName = 'ValidationTooltip';
